import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
    getTenantLabourRates,
    createTenantLabourRate,
    updateTenantLabourRate as updateTenantLabourRateApi,
    deleteTenantLabourRate as deleteTenantLabourRateApi,
    TenantLabourRateItem,
    TenantLabourRatePayload
} from "../../services/tenantLabourRateService";

interface TenantLabourRateState {
    tenantLabourRates: TenantLabourRateItem[];
    loading: boolean;
    error: string | null;
    totalCount: number;
    totalPages: number;
    currentPage: number;
}

const initialState: TenantLabourRateState = {
    tenantLabourRates: [],
    loading: false,
    error: null,
    totalCount: 0,
    totalPages: 0,
    currentPage: 1,
};

export const fetchTenantLabourRates = createAsyncThunk(
    "tenantLabourRates/fetch",
    async ({ pageNumber = 1, pageSize = 50 }: { pageNumber?: number; pageSize?: number } = {}, { rejectWithValue }) => {
        try {
            const response = await getTenantLabourRates(pageNumber, pageSize);
            return response;
        } catch (error: any) {
            return rejectWithValue(error);
        }
    }
);

export const addTenantLabourRate = createAsyncThunk(
    "tenantLabourRates/add",
    async (data: TenantLabourRatePayload, { rejectWithValue }) => {
        try {
            const response = await createTenantLabourRate(data);
            return response;
        } catch (error: any) {
            return rejectWithValue(error);
        }
    }
);

export const updateTenantLabourRate = createAsyncThunk(
    "tenantLabourRates/update",
    async ({ id, data }: { id: string; data: TenantLabourRatePayload }, { rejectWithValue }) => {
        try {
            const response = await updateTenantLabourRateApi(id, data);
            return response;
        } catch (error: any) {
            return rejectWithValue(error);
        }
    }
);

export const deleteTenantLabourRate = createAsyncThunk(
    "tenantLabourRates/delete",
    async (id: string, { rejectWithValue }) => {
        try {
            await deleteTenantLabourRateApi(id);
            return id;
        } catch (error: any) {
            return rejectWithValue(error);
        }
    }
);

const tenantLabourRateSlice = createSlice({
    name: "tenantLabourRates",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        // Fetch
        builder.addCase(fetchTenantLabourRates.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchTenantLabourRates.fulfilled, (state, action: any) => {
            state.loading = false;
            if (action.payload && action.payload.items) {
                state.tenantLabourRates = action.payload.items;
                state.totalCount = action.payload.totalCount;
                state.totalPages = action.payload.totalPages;
                state.currentPage = action.payload.pageNumber;
            } else if (Array.isArray(action.payload)) {
                state.tenantLabourRates = action.payload;
            } else {
                state.tenantLabourRates = [];
            }
        });
        builder.addCase(fetchTenantLabourRates.rejected, (state, action: any) => {
            state.loading = false;
            state.error = action.payload || "Failed to fetch labour rates";
        });

        // Add
        builder.addCase(addTenantLabourRate.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(addTenantLabourRate.fulfilled, (state, action: any) => {
            state.loading = false;
            // Optimistic add or refetch will handle this.
            // If we want to add to list immediately:
            // state.tenantLabourRates.push(action.payload);
        });
        builder.addCase(addTenantLabourRate.rejected, (state, action: any) => {
            state.loading = false;
            state.error = action.payload || "Failed to add labour rate";
        });

        // Update
        builder.addCase(updateTenantLabourRate.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(updateTenantLabourRate.fulfilled, (state, action: any) => {
            state.loading = false;
            const index = state.tenantLabourRates.findIndex((item) => item.id === action.payload.id);
            if (index !== -1) {
                state.tenantLabourRates[index] = action.payload;
            }
        });
        builder.addCase(updateTenantLabourRate.rejected, (state, action: any) => {
            state.loading = false;
            state.error = action.payload || "Failed to update labour rate";
        });

        // Delete
        builder.addCase(deleteTenantLabourRate.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(deleteTenantLabourRate.fulfilled, (state, action: any) => {
            state.loading = false;
            state.tenantLabourRates = state.tenantLabourRates.filter((item) => item.id !== action.payload);
        });
        builder.addCase(deleteTenantLabourRate.rejected, (state, action: any) => {
            state.loading = false;
            state.error = action.payload || "Failed to delete labour rate";
        });
    },
});

export default tenantLabourRateSlice.reducer;

export const selectTenantLabourRates = (state: any) => state.TenantLabourRates.tenantLabourRates;
export const selectTenantLabourRatesLoading = (state: any) => state.TenantLabourRates.loading;
export const selectTenantLabourRatesError = (state: any) => state.TenantLabourRates.error;
